// apps/web/src/components/painel/home/assinatura-ncs-por-etapa.tsx — Fase 17.8, Plano 18 (D-12; UI-SPEC U-20, U-24).
// Assinatura "Onde o defeito nasce · NCs por etapa e cabine · 30 dias" da Qualidade: até 6 linhas (as de mais NCs primeiro, como a API
// devolve), cada uma com o rótulo (Serviço e código da célula), a contagem e uma barra de 8px no gradiente do perfil cuja largura é
// proporcional ao maior total da lista. O trecho das NCs ainda abertas há mais de `limiteDias` é um segmento em --color-warning, com
// texto oculto "antigas" (a cor nunca é o único sinal). Cada linha é um link para as NCs quando a sessão pode abrir a rota. Sem NCs em
// 30 dias: uma linha amigável com ShieldCheck. O limite de dias vem da resposta, nunca de um literal.
import type { CSSProperties, ReactElement } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { NcsPorOrigem } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { formatarNumero } from '../../../lib/painel/home/formato';
import { EstadoVazio } from './estado-vazio';
import '../../../styles/painel-home.css';

export const TEXTO_SEM_NCS = 'Nenhuma não conformidade nos últimos 30 dias.';

/** Linhas da assinatura (a API devolve no máximo este tanto; a tela corta igual por segurança). */
export const LINHAS_NCS_POR_ETAPA = 6;

export interface AssinaturaNcsPorEtapaProps {
  readonly origens: readonly NcsPorOrigem[];
  /** Limite (em dias) a partir do qual uma NC aberta é antiga, vindo da resposta. */
  readonly limiteDias: number;
  readonly permissoes: readonly string[];
}

/** Largura da barra (% com 1 casa) sobre o maior total da lista; maior total inválido não gera barra. */
export function larguraDaBarra(total: number, maiorTotal: number): number {
  if (!Number.isFinite(maiorTotal) || maiorTotal <= 0 || !Number.isFinite(total) || total <= 0) return 0;
  return Math.min(100, Math.round((total / maiorTotal) * 1000) / 10);
}

const contagem = (n: number): string => `${formatarNumero(n)} ${n === 1 ? 'NC' : 'NCs'}`;
const antigas = (n: number): string => `${formatarNumero(n)} ${n === 1 ? 'antiga' : 'antigas'}`;

function Linha({ origem, maiorTotal, limiteDias, permissoes }: { readonly origem: NcsPorOrigem; readonly maiorTotal: number; readonly limiteDias: number; readonly permissoes: readonly string[] }): ReactElement {
  const velhas = Math.min(Math.max(origem.antigas, 0), origem.total);
  const normais = origem.total - velhas;
  // A barra ocupa a proporção do total sobre o maior total; dentro dela o trecho das antigas divide o espaço pelas NCs.
  const estilo = { '--w-total': `${larguraDaBarra(origem.total, maiorTotal)}%`, '--g-antigas': velhas, '--g-normais': normais } as CSSProperties;
  const nomeAcessivel =
    velhas > 0
      ? `${origem.rotulo}: ${contagem(origem.total)}, ${antigas(velhas)} (abertas há mais de ${formatarNumero(limiteDias)} ${limiteDias === 1 ? 'dia' : 'dias'}).`
      : `${origem.rotulo}: ${contagem(origem.total)}.`;
  const corpo = (
    <>
      <span className="painel-ncs__topo">
        <span className="painel-ncs__rotulo">{origem.rotulo}</span>
        <span className="painel-ncs__num">{formatarNumero(origem.total)}</span>
      </span>
      <span className="painel-ncs__barra" style={estilo} aria-hidden="true">
        {normais > 0 && <span className="painel-ncs__segmento painel-ncs__segmento--normais" />}
        {velhas > 0 && <span className="painel-ncs__segmento painel-ncs__segmento--antigas" />}
      </span>
      {velhas > 0 && <span className="sr-only">{antigas(velhas)}</span>}
    </>
  );
  return (
    <li className="painel-ncs__linha" data-testid="painel-ncs-origem" data-antigas={velhas > 0 ? 'true' : undefined}>
      {podeAbrirRota(origem.href, permissoes) ? (
        <a className="painel-ncs__link" href={origem.href} aria-label={nomeAcessivel}>
          {corpo}
        </a>
      ) : (
        <div className="painel-ncs__link" role="group" aria-label={nomeAcessivel}>
          {corpo}
        </div>
      )}
    </li>
  );
}

export function AssinaturaNcsPorEtapa({ origens, limiteDias, permissoes }: AssinaturaNcsPorEtapaProps): ReactElement {
  const visiveis = origens.slice(0, LINHAS_NCS_POR_ETAPA);
  const maiorTotal = Math.max(0, ...visiveis.map((origem) => origem.total));
  const temAntigas = visiveis.some((origem) => origem.antigas > 0);
  return (
    <section className="painel-assinatura" data-testid="painel-assinatura" data-tipo="ncs-por-etapa" aria-label="Onde o defeito nasce">
      <header className="painel-assinatura__cabecalho">
        <h3 className="painel-assinatura__titulo">Onde o defeito nasce</h3>
        <span className="painel-assinatura__subtitulo">NCs por etapa e cabine · 30 dias</span>
      </header>
      {visiveis.length === 0 ? (
        <EstadoVazio icone={ShieldCheck} texto={TEXTO_SEM_NCS} />
      ) : (
        <>
          <ul className="painel-ncs">
            {visiveis.map((origem) => (
              <Linha key={origem.origemId} origem={origem} maiorTotal={maiorTotal} limiteDias={limiteDias} permissoes={permissoes} />
            ))}
          </ul>
          {temAntigas && (
            <div className="painel-ncs__legenda">
              <span className="painel-ncs__chave">
                <span className="painel-ncs__quadrado painel-ncs__quadrado--antigas" aria-hidden="true" />
                {`abertas há mais de ${formatarNumero(limiteDias)} ${limiteDias === 1 ? 'dia' : 'dias'}`}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
